import { format } from "date-fns";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useEffect, useState } from "react";
import { IoEyeSharp } from "react-icons/io5";
import { RxCross1 } from "react-icons/rx";
import { useNavigate } from "react-router-dom";
import { LIVE_URL } from "../../../../config/config";
import useAuthToken from "../../../../hooks/useAuthToken";
import ReportDetail from "../ReportDetail";
import styles from "./css/ProductTable.module.scss";

const ProductTable = ({
  apiEndpoint,
  rows,
  reports,
  setReports,
  currentPage,
  setCurrentPage,
  sortField,
  sortOrder,
  setSortField,
  setSortOrder,
  loading,
  setLoading,
  refreshReports,
}) => {
  const { token } = useAuthToken();
  const [products, setProducts] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [popupText, setPopupText] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();

  //  Single fetchReports defined at top level — used by both
  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await fetch(apiEndpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();
      setProducts(data.data.user);
      setReports(data.data.user);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  //  Only one useEffect for initial list fetch
  useEffect(() => {
    fetchReports();
  }, [apiEndpoint, rows]);

  const statusInfo = {
    1: { label: "Pending", className: styles["status-pending"] },
    2: { label: "Warning", className: styles["status-warning"] },
    3: { label: "Blocked", className: styles["status-blocked"] },
  };

  //  fetchUserReport is defined AND called correctly inside useEffect
  useEffect(() => {
    if (!selectedUser) return;

    const fetchUserReport = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${LIVE_URL}/admin/list-user-report-details/${selectedUser}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        const json = await res.json();
        const { user, reported_users, report_summery } = json.data;

        const mappedUser = {
          report_user_id: user.id,
          report_user: {
            firstname: user.firstname,
            lastname: user.lastname,
            username: user.email?.split("@")[0],
          },
          max_status: user.status,
          report_count: report_summery.totalReports,
          reason_breakdown: report_summery.report_categories.map((cat) => ({
            reason: cat.title,
            count: cat.count,
          })),
          report_details: reported_users.map((r) => ({
            reporter_name: r.user?.firstname ?? "Unknown",
            created_at: r.created_at,
            reason: r.category?.title ?? "—",
            comment: r.message,
          })),
        };

        setUserData(mappedUser);
      } catch (err) {
        console.error("fetchUserReport error:", err);
      } finally {
        setLoading(false);
      }
    };

    //  Call is INSIDE useEffect, AFTER the function definition
    fetchUserReport();
  }, [selectedUser]);

  const columns = [
    {
      field: "report_user_id",
      header: "Sr No.",
      className: styles["column-id"],
      sortable: true,
    },
    {
      field: "report_user.firstname",
      header: "Report User",
      className: styles["column-report-user"],
      sortable: true,
    },
    {
      field: "report_count",
      header: "Reports",
      className: styles["column-report-count"],
      sortable: true,
    },
    {
      field: "max_status",
      header: "Status",
      className: styles["column-status"],
      sortable: false,
      body: (rowData) => {
        const info = statusInfo[rowData.report_user?.status];
        if (!info) return <span style={{ color: "#666" }}>—</span>;
        return (
          <span className={`${styles.badge} ${info.className}`}>
            {info.label}
          </span>
        );
      },
    },
    {
      field: "max_created_at",
      header: "Date",
      className: styles["column-date"],
      body: (rowData) => {
        if (!rowData.max_created_at) return "—";
        const date = new Date(rowData.max_created_at);
        if (isNaN(date.getTime())) return "—";
        return format(date, "dd-MM-yyyy");
      },
      sortable: true,
    },
    {
      field: "",
      header: "View",
      className: styles["column-actions"],
      sortable: false,
      body: (rowData) => (
        <IoEyeSharp
          className={styles.eyeIcon}
          title="View Reports"
          onClick={(e) => {
            e.stopPropagation();
            handleEdit(rowData.report_user_id);
          }}
        />
      ),
    },
  ];

  const handlePageChange = (event) => {
    setCurrentPage(event.page + 1);
  };

  const handleSort = (event) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder === 1 ? "ASC" : "DESC");
    setCurrentPage(1);
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupText("");
  };

  const handleEdit = (id) => {
    setSelectedUser(id);
  };

  const handleClose = () => {
    setSelectedUser(null);
    setUserData(null);
  };

  const handleActionSuccess = (updatedUserId, newStatus) => {
    handleClose();
    refreshReports();
  };

  return (
    <>
      <div className={`${styles.main}`}>
        <DataTable
          className={`${styles.datatable}`}
          value={reports}
          rows={rows}
          first={(currentPage - 1) * rows}
          onPage={handlePageChange}
          totalRecords={reports.length}
          sortField={sortField}
          sortOrder={sortOrder === "ASC" ? 1 : -1}
          onSort={handleSort}
          emptyMessage={
            loading ? (
              <div className={styles.loading_row}>
                <div className={styles.spinner}></div>
              </div>
            ) : (
              <div className={styles["no-data-message"]}>
                No data to display.
              </div>
            )
          }
        >
          {columns.map((col, i) => (
            <Column
              key={i}
              field={col.field}
              header={col.header}
              sortable={col.sortable}
              headerClassName={`${styles["column-header-small"]} ${col.className}`}
              bodyClassName={col.className}
              body={col.body}
              handleSort={handleSort}
            />
          ))}
        </DataTable>
      </div>

      {showPopup && (
        <div className={styles.popupOverlay} onClick={closePopup}>
          <div className={styles.popupBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.popupContent}>
              <p>{popupText}</p>
              <RxCross1 className={styles.closeButton} onClick={closePopup} />
            </div>
          </div>
        </div>
      )}

      {userData && selectedUser && (
        <ReportDetail
          user={userData}
          onClose={handleClose}
          onActionSuccess={handleActionSuccess}
        />
      )}
    </>
  );
};

export default ProductTable;
